import { type ReactNode, useId } from 'react';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';

import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import { InfoTooltip } from '../../../shared/ui/forms/InfoTooltip';
import { FieldErrorText } from '../../../shared/ui/forms/FieldErrorText';
import { VerticalDivider } from '../../../shared/ui/VerticalDivider';

export interface ConfigFieldProps {
  readonly label: string;
  /** Info-tooltip help text shown next to the label. */
  readonly info: string;
  /** Accessible label for the info-icon trigger. */
  readonly infoAltText: string;
  readonly isRequired?: boolean;
  readonly children?: ReactNode;
  /**
   * Alternative to `children` for controls that need the label element's id
   * (e.g. a Slider wired via `aria-labelledby`) or the error-text id (to wire
   * their own `aria-describedby`). Receives both ids; `errorId` is set only
   * while an `error` is showing, so a caller can spread it conditionally.
   */
  readonly renderLabel?: (ids: ConfigFieldIds) => ReactNode;
  /** Localized inline-error message. When set, a critical-red footer renders
   *  below the control (matching the shared FormField error footer). The footer
   *  carries `role="alert"`, so a screen reader announces the message when it
   *  appears; its id is handed to the control via `renderLabel`'s `errorId` so
   *  a focus-time `aria-describedby` association can also be wired where the
   *  control supports it (the plain text Input does; the custom Stencil selects
   *  rely on the alert announcement). The label stays neutral — only the footer
   *  carries the red, consistent with the FormField-wrapped fields. */
  readonly error?: string;
}

/** The ids {@link ConfigField} hands to a `renderLabel` control so it can wire
 *  its own `aria-labelledby` / `aria-describedby`. */
export interface ConfigFieldIds {
  /** Id of the visible label text — for the control's `aria-labelledby`. */
  readonly labelId: string;
  /** Id of the error footer text — present only while an error is showing, for
   *  the control's `aria-describedby`. */
  readonly errorId?: string;
}

/**
 * One labelled configuration field on the Question Configuration step: a label
 * row (`RequiredLabel` + `VerticalDivider` + `InfoTooltip`) above its control.
 * When the control needs the label's element id, pass `renderLabel` to receive
 * it; otherwise pass the control as `children`. Shared by the Basic
 * Information and Documentation cards so the label-row structure lives in one
 * place.
 */
export function ConfigField({
  label,
  info,
  infoAltText,
  isRequired = false,
  children,
  renderLabel,
  error,
}: ConfigFieldProps): JSX.Element {
  const labelId = useId();
  const errorId = useId();
  const hasError = error !== undefined;
  // Only hand the control an errorId while an error is actually showing, so a
  // conditional `aria-describedby` spread points at a rendered element.
  const ids: ConfigFieldIds = hasError ? { labelId, errorId } : { labelId };
  return (
    <Col gridGap="dimensions.spacing.100">
      <Row alignItems="center" gridGap="dimensions.spacing.200">
        {/* id lives on the label text only (not the whole Row): a control
         *  wired via aria-labelledby={labelId} must resolve to just the field
         *  label, not the label PLUS the info-icon's aria-label. The asterisk
         *  inside RequiredLabel is aria-hidden, so it's excluded from the name.
         *  The label stays neutral on error (hasError={false}) — only the
         *  footer below carries the critical red, matching FormField. */}
        <View id={labelId}>
          <RequiredLabel isRequired={isRequired} hasError={false}>
            {label}
          </RequiredLabel>
        </View>
        <VerticalDivider />
        <InfoTooltip text={info} ariaLabel={infoAltText} />
      </Row>
      {renderLabel ? renderLabel(ids) : children}
      {hasError ? <FieldErrorText id={errorId}>{error}</FieldErrorText> : null}
    </Col>
  );
}
